import Highlight from "@/components/home/Highlight";
import { Head, Link, usePage } from "@inertiajs/react";
import {
  ArrowRight,
  BookOpenCheck,
  CreditCard,
  FileSpreadsheet,
  QrCode,
  Trophy,
  Users,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import FaqCard from "@/components/marketing/FaqCard";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";

// Cluster spoke pages — /readathon-software is the hub, so it links down to
// each focused guide. Content pages live behind the catch-all route, so
// these are plain paths rather than named shared routes.
const GUIDE_LINKS = [
  { href: "/readathon-software-canada", label: "Read-a-thon software for Canadian schools" },
  { href: "/readathon-software-for-elementary-schools", label: "Read-a-thon software for elementary schools" },
  { href: "/readathon-app-for-schools", label: "Read-a-thon app for schools" },
  { href: "/readathon-donation-pages", label: "Read-a-thon donation pages" },
  { href: "/readathon-fundraising-ideas", label: "Read-a-thon fundraising ideas" },
  { href: "/readathon-tracking-spreadsheet-alternative", label: "Read-a-thon tracking spreadsheet alternative" },
];

export default function ReadathonSoftware({ faqs = [] }) {
  const { routes } = usePage().props;

  return (
    <MarketingLayout>
      <Head title="Readathon software and app for schools" />

      <PageContainer width="wide" className="pt-10 md:pt-14 pb-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.05fr_0.95fr] items-center">
          <div className="space-y-6">
            <Badge className="uppercase tracking-wider">Readathon software</Badge>
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl leading-tight text-foreground">
                Readathon software that handles the whole <Highlight color="forest">fundraiser.</Highlight>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                PagePledge is a readathon app for schools that need online donations,
                reading logs, QR-coded take-home sheets, student pages, leaderboards,
                and rewards in one place.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" asChild>
                <Link href={routes.new_school_application}>
                  Sign up your school
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href={routes.how_it_works}>See how it works</Link>
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card/70 p-5 shadow-md">
            <div className="grid gap-3">
              <PreviewRow icon={<FileSpreadsheet />} label="Import classrooms" value="CSV upload" />
              <PreviewRow icon={<QrCode />} label="Parent access" value="QR sheets" />
              <PreviewRow icon={<BookOpenCheck />} label="Reading minutes" value="Home + class logs" />
              <PreviewRow icon={<CreditCard />} label="Donations" value="Square or Stripe" />
              <PreviewRow icon={<Trophy />} label="Motivation" value="Goals + rewards" />
            </div>
          </div>
        </div>
      </PageContainer>

      <PageContainer width="wide" className="pt-10">
        <SectionHeading
          flourish="built for school fundraisers"
          title="Everything a readathon app has to keep straight."
          subhead="A good readathon campaign is part classroom challenge, part parent workflow, part donor checkout, and part school office report."
        />

        <div className="grid gap-5 md:grid-cols-3 mt-10">
          <FeatureCard
            icon={<Users />}
            title="School setup without busywork"
            text="Import students and classrooms from a spreadsheet, invite teachers, set campaign dates, and generate each student's public link automatically."
          />
          <FeatureCard
            icon={<BookOpenCheck />}
            title="Reading logs for home and class"
            text="Parents can log at-home reading, while teachers can log a whole classroom in one pass. Minutes feed goals, dashboards, and rankings."
          />
          <FeatureCard
            icon={<CreditCard />}
            title="Connect Square or Stripe"
            text="Connect your school’s Square or Stripe account to receive card donations. The provider charges processing fees, which donors can choose to cover. PagePledge adds no transaction fees."
          />
          <FeatureCard
            icon={<QrCode />}
            title="QR-coded take-home sheets"
            text="Every student gets a printable sheet with their activation code and QR link, so families can start without passwords or manual account setup."
          />
          <FeatureCard
            icon={<Trophy />}
            title="Leaderboards, goals, and rewards"
            text="Track school-wide totals, classroom progress, student prizes, and milestone rewards without rebuilding spreadsheets every day."
          />
          <FeatureCard
            icon={<FileSpreadsheet />}
            title="Reports schools can use"
            text="Admins can see donations, payment status, reading activity, classroom progress, and final results from the same campaign dashboard."
          />
        </div>
      </PageContainer>

      <PageContainer width="default" className="pt-10">
        <SectionHeading
          align="left"
          flourish="why schools switch"
          title="Less manual tracking. More money kept by the school."
          subhead="Readathon software should reduce office work and make the fundraiser easier for families, not skim a percentage from every donation."
        />

        <div className="grid gap-4 mt-8">
          {[
            "A flat campaign fee with an early adopter discount. No PagePledge transaction fees.",
            "Donations go to your school’s connected Square or Stripe account.",
            "Student pages are shareable, but not meant for Google indexing.",
            "Parents, teachers, donors, and school admins each get the workflow they need.",
            "Built around Canadian schools, Canadian currency, and school-run campaigns.",
          ].map((item) => (
            <div key={item} className="flex gap-3 text-sm md:text-base text-muted-foreground">
              <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </PageContainer>

      <PageContainer width="wide" className="pt-12">
        <SectionHeading
          flourish="common questions"
          title="Readathon software FAQ"
        />
        <div className="grid gap-4 md:grid-cols-2 mt-10">
          {faqs.map((faq) => (
            <FaqCard key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </PageContainer>

      <PageContainer width="default" className="pt-10">
        <SectionHeading
          align="left"
          flourish="go deeper"
          title="Read-a-thon guides"
          subhead="Learn more about running a read-a-thon at your school, from choosing software to collecting donations."
        />
        <div className="grid gap-3 mt-8 md:grid-cols-2">
          {GUIDE_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="flex items-center justify-between gap-4 rounded-md border border-border/70 bg-card/50 p-4 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <span>{link.label}</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          ))}
        </div>
      </PageContainer>

      <PageContainer width="narrow" className="text-center space-y-6 pb-12 md:pb-16">
        <SectionHeading
          flourish="early adopter discount"
          title="Bring your next readathon online."
          subhead="Get an early adopter discount on a simpler readathon app for donations, reading logs, and campaign tracking."
        />
        <Button size="lg" asChild>
          <Link href={routes.new_school_application}>
            Sign up your school
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </PageContainer>
    </MarketingLayout>
  );
}

function PreviewRow({ icon, label, value }) {
  return (
    <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4 rounded-md border border-border/70 bg-background/70 p-4">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-primary [&_svg]:h-5 [&_svg]:w-5 shrink-0">{icon}</span>
        <span className="font-medium text-foreground">{label}</span>
      </div>
      <span className="text-sm text-muted-foreground pl-8 sm:pl-0 sm:text-right">{value}</span>
    </div>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <Card className="bg-card/70">
      <CardHeader>
        <div className="text-primary mb-2 [&_svg]:h-6 [&_svg]:w-6">{icon}</div>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription className="text-sm leading-relaxed">{text}</CardDescription>
      </CardHeader>
    </Card>
  );
}
